import { Fragment, useEffect, useRef, useState } from 'react'
import { api, type Billing } from '../lib/api'
import { money, currentPeriod, today } from '../lib/format'
import { Panel, Alert, Loading, Empty, Tag, DocHead } from '../components/ui'
import { MonthPicker } from '../components/MonthRangePicker'
import { downloadElementAsPdf } from '../lib/pdfExport'

interface WriterGroup {
  writer: string
  rows: Billing[]
  qty: number
  amount: number
}

/**
 * 依寫手分組。同一位寫手的列集中在一起（寫手先後順序照原本第一次出現的位置排，不另外
 * 排序），名字只印一次、用 rowspan 蓋住整組——名字逐列重複的表掃起來分不出一個人到底
 * 做了幾筆。同一位寫手有兩筆以上時，組末補一列小計，財務才不必自己心算匯款金額。
 */
function groupByWriter(rows: Billing[]): WriterGroup[] {
  const groups: WriterGroup[] = []
  for (const r of rows) {
    const g = groups.find((g) => g.writer === r.writer_name)
    if (g) g.rows.push(r)
    else groups.push({ writer: r.writer_name, rows: [r], qty: 0, amount: 0 })
  }
  for (const g of groups) {
    g.qty = g.rows.reduce((n, r) => n + r.qty, 0)
    g.amount = g.rows.reduce((n, r) => n + r.amount, 0)
  }
  return groups
}

/**
 * 請款明細表。內包與外包各自出一張表：兩者的簽核流程與帳務歸屬不同，
 * 混在一起印出去對不了帳，所以用同一個元件、以 dispatch 區分。
 */
export default function Billings({ dispatch = '外包' }: { dispatch?: '外包' | '內包' }) {
  const [period, setPeriod] = useState(currentPeriod())
  const [status, setStatus] = useState('')
  const [data, setData] = useState<{ rows: Billing[]; total_qty: number; total_amount: number } | null>(null)
  const [error, setError] = useState('')

  const load = () => {
    setData(null)
    const qs = new URLSearchParams({ period, dispatch_type: dispatch })
    if (status) qs.set('status', status)
    api.get<any>(`/billings?${qs}`).then(setData).catch((e) => setError(e.message))
  }

  useEffect(load, [period, status, dispatch])

  /*
   * 「同步預覽 / 寫入 Google Sheet / 整月標記已請款」三顆按鈕先從這一頁收起來。
   * 後端的 /api/sync 與 /api/billings/mark-paid-period 都還在，要恢復時把下面這段
   * 還原、按鈕列補回三顆按鈕，並把 msg 提示區加回來即可。
   */
  // const sync = async (mode: 'preview' | 'execute') => {
  //   setError(''); setMsg('')
  //   try {
  //     const res = await api.post<any>(`/sync/${mode}`, { target: 'billing', period })
  //     setMsg(
  //       mode === 'preview'
  //         ? `預覽：${res.row_count} 列，範圍 ${res.range}，將變動 ${res.changes.length} 個儲存格` +
  //           (res.truncated_warning ? `\n⚠ ${res.truncated_warning}` : '')
  //         : `已寫入 Google Sheet：${res.range}，共 ${res.updatedCells} 個儲存格`
  //     )
  //   } catch (e: any) {
  //     setError(e.message)
  //   }
  // }
  //
  // const markPaid = async () => {
  //   if (!confirm(`確定將 ${period} 的${dispatch}「待請款」全部標記為已請款？`)) return
  //   try {
  //     const res = await api.post<{ updated: number }>('/billings/mark-paid-period', { period, dispatch_type: dispatch })
  //     setMsg(`已標記 ${res.updated} 筆為已請款`)
  //     load()
  //   } catch (e: any) {
  //     setError(e.message)
  //   }
  // }

  const groups = groupByWriter(data?.rows ?? [])

  /** 列印版那一塊的 DOM，另存 PDF 要拿它去渲染 */
  const docRef = useRef<HTMLDivElement>(null)
  const [pdfBusy, setPdfBusy] = useState(false)

  /* 另存 PDF：跟「列印」分開。列印對話框存出來的檔案會受各人的縮放與頁首頁尾設定影響，
     這裡固定參數輸出，寄給財務或客戶的版本才會一致 */
  const savePdf = async () => {
    if (!docRef.current) return
    setError(''); setPdfBusy(true)
    try {
      await downloadElementAsPdf(docRef.current, `請款明細_${dispatch}_${period}.pdf`)
    } catch (e: any) {
      setError(`產生 PDF 失敗：${e?.message || e}`)
    } finally {
      setPdfBusy(false)
    }
  }

  return (
    <>
      <div className="page-head">
        <h2>請款明細（{dispatch === '內包' ? '內' : '外'}）</h2>
        <span className="hint">請款月份</span>
        <MonthPicker value={period} onChange={setPeriod} />
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 110 }}>
          <option value="">全部</option>
          <option value="待請款">待請款</option>
          <option value="已請款">已請款</option>
        </select>
        <div className="spacer" />
        <div className="btn-row">
          <button className="primary" onClick={savePdf} disabled={!data || data.rows.length === 0 || pdfBusy}>
            {pdfBusy ? '產生中…' : '另存 PDF'}
          </button>
          <button onClick={() => window.print()}>列印</button>
        </div>
      </div>

      <Alert kind="error">{error}</Alert>

      {!data ? (
        <Loading />
      ) : data.rows.length === 0 ? (
        <Panel><Empty>這個月還沒有可請款的項目（驗收通過後會自動產生）</Empty></Panel>
      ) : (
        <div className="print-doc billing-doc" ref={docRef}>
          {/* 與發包申請一致：申請日期取列印當天（台北時區），不是資料所屬的月份 */}
          <DocHead
            lead={dispatch === '內包' ? '文案內部發包' : '文案外部發包'}
            title="請款明細"
            sub={`申請日期：${today().replace(/-/g, ' / ')}`}
            subAlign="right"
          />

          {/* 每一欄都不換行；寬度不夠時由外層 .table-wrap 橫向捲動、列印則自動縮排 */}
          <table className="nowrap">
            <thead>
              <tr>
                <th style={{ width: 32 }}>序</th>
                <th>寫手</th>
                <th>發包單號</th>
                <th>驗收單號</th>
                <th>客戶</th>
                <th>合約編號</th>
                <th>案件類型</th>
                <th className="num">篇數</th>
                <th className="num">單價</th>
                <th className="num">金額</th>
                <th>備註</th>
              </tr>
            </thead>
            <tbody>
              {/* 序號逐列遞增，跨寫手也不重來；小計列不佔序號 */}
              {(() => {
                let seq = 0
                return groups.map((g) => {
                  // 只有一筆的寫手不需要小計——那一列本身就是小計
                  const withSub = g.rows.length > 1
                  return (
                    <Fragment key={g.writer}>
                      {/* 同一位寫手的列之間畫淺線，區塊與區塊之間才靠深線分開 */}
                      {g.rows.map((b, i) => (
                        <tr key={b.id} className={withSub ? 'in-group' : undefined}>
                          <td>{++seq}</td>
                          {i === 0 && <td className="writer" rowSpan={g.rows.length}>{b.writer_name}</td>}
                          <td>{b.work_order_code}</td>
                          <td>{b.acceptance_code}</td>
                          <td>{b.client_name}</td>
                          <td>{b.contract_code}</td>
                          <td>{b.job_type_name}</td>
                          <td className="num">{b.qty}</td>
                          <td className="num">{money(b.unit_price)}</td>
                          <td className="num">{money(b.amount)}</td>
                          <td>
                            {b.note} <span className="no-print"><Tag value={b.status} /></span>
                          </td>
                        </tr>
                      ))}
                      {withSub && (
                        <tr className="subtotal">
                          {/* 序與寫手兩欄一起併進小計列，整列灰底才不會缺一角 */}
                          <td colSpan={2}></td>
                          {/* 「小計」放在案件類型欄並靠右，跟合計列的「總篇數」同一個排法 */}
                          <td colSpan={4}></td>
                          <td className="num">小計</td>
                          <td className="num">{g.qty}</td>
                          <td></td>
                          <td className="num">{money(g.amount)}</td>
                          <td></td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })
              })()}
            </tbody>
            <tfoot>
              {/* 合計列：標籤與空白格用 th 上灰底，只有數字格留白 */}
              <tr>
                <th colSpan={6}></th>
                <th>總篇數</th>
                <td className="num">{data.total_qty}</td>
                <th>總額</th>
                <td className="num">{money(data.total_amount)}</td>
                <th>—</th>
              </tr>
            </tfoot>
          </table>

          {/* 簽核區與發包單、驗收單同一版式：灰底標題列標職稱，下方整格留白給簽名。
              六個簽核欄分兩組，每組都是「標題列 + 簽名列」 */}
          <h2 style={{ marginTop: 28 }}>簽核</h2>
          <table className="sign-table">
            <tbody>
              <tr>
                <th style={{ width: '34%' }}>直屬主管</th>
                <th style={{ width: '33%' }}>部門主管</th>
                <th>總經理</th>
              </tr>
            </tbody>
            <tbody className="sign-row">
              <tr><td></td><td></td><td></td></tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
